import { useEffect } from "react";

import { useCloseNavDrawerOnMount } from "../../lib/nav-drawer";
import { cn } from "./cn";

/**
 * The dialog every popup in Pulse opens in.
 *
 * Escape, the cross and clicking away all close it -- three ways out, because
 * these open while somebody is deciding whether to ring or which way to drive,
 * and the one thing worse than a screen you did not want is a screen you cannot
 * leave.
 *
 * The backdrop click is guarded on the event target so a drag that starts
 * inside the card and finishes outside it -- selecting a phone number or an
 * address, most obviously -- does not dismiss what you were reading.
 *
 * ## Why this is one component and not thirteen
 *
 * It said "shared rather than declared per page ... two copies would drift into
 * two behaviours on a phone, which nobody would report as a bug", and then
 * eight dialogs were written with their own `fixed inset-0` anyway. They
 * drifted exactly as predicted: five different paddings, three different
 * scrims, and -- the part that actually broke -- six of them with no scrolling
 * of any kind. On a handset the new-meeting form is taller than the screen, so
 * its Cancel and Book it sat below the bottom edge with nothing to scroll:
 * the dialog could be filled in and not submitted.
 *
 * ## Height is the whole problem
 *
 * A phone is 600-700px of usable height and a form with six fields is taller
 * than that, so **the card is capped and its body scrolls**, rather than the
 * card being allowed to grow and the backdrop scrolling underneath it. The
 * difference matters: a pinned `footer` keeps the actions on screen the entire
 * time, so there is never a state where the thing you came to press is
 * somewhere off the bottom.
 *
 * Measured in `dvh`, not `vh`. On iOS the address bar collapses as you scroll
 * and `100vh` is the *tall* viewport -- the one with the toolbars hidden -- so
 * a card sized in `vh` runs its last 60-90px underneath Safari's own chrome,
 * which is precisely the "coming off the page" this fixes. `dvh` follows the
 * space actually available.
 */

const SIZES = {
  sm: "sm:max-w-md",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-3xl",
} as const;

export function Overlay({
  onClose,
  title,
  footer,
  size = "xl",
  padded = true,
  children,
}: {
  onClose: () => void;
  /**
   * Drawn as a heading row that does not scroll.
   *
   * Optional: a dialog that is one paragraph does not need a title bar, and
   * gets the bare cross in the corner instead.
   */
  title?: React.ReactNode;
  /**
   * The actions, pinned to the bottom of the card.
   *
   * Worth using for anything with a form in it. A footer inside `children`
   * scrolls away with the fields, which on a phone means the button somebody
   * is looking for is below the fold of a box that is already the whole screen.
   */
  footer?: React.ReactNode;
  size?: keyof typeof SIZES;
  /** Off for a dialog that draws its own padding, like a preview. */
  padded?: boolean;
  children: React.ReactNode;
}) {
  useCloseNavDrawerOnMount();

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      /* `visible` and `pointer-events-auto` because a dialog opened from the
         phone's drawer outlives the drawer closing round it, and the drawer
         hides itself with the inheritable opposites -- see `AppFrame`. */
      className="pointer-events-auto visible fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-3 sm:p-6 sm:pt-12"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          "relative flex w-full flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-xl",
          /* The cap, and the reason the actions stay reachable. `max-h-full`
             would do it on a desktop and not on iOS -- see the header. */
          "max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-6rem)]",
          SIZES[size],
        )}
      >
        {title ? (
          <header className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-3">
            <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-content">
              {title}
            </h2>
            <Close onClose={onClose} />
          </header>
        ) : (
          <div className="absolute right-3 top-3 z-10">
            <Close onClose={onClose} />
          </div>
        )}

        {/*
          `min-h-0` is what makes the cap above do anything: a flex child's
          default minimum is its content, so without it the body refuses to
          shrink and the card grows past the cap it was given.
        */}
        <div
          className={cn(
            "min-h-0 flex-1 overflow-y-auto overscroll-contain",
            padded && (title ? "p-4" : "p-4 sm:p-6"),
          )}
        >
          {children}
        </div>

        {footer ? (
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}

function Close({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className={cn(
        /* A thumb's size on a phone, where it is the only way out without a
           keyboard; the corner's own size on a desktop. The negative margins
           take the extra back out of the title bar, which stays its height. */
        "flex size-9 shrink-0 items-center justify-center rounded-md text-faint",
        "-my-1.5 -mr-1.5 md:my-0 md:mr-0 md:size-7",
        "hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
      )}
    >
      ✕
    </button>
  );
}
